import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { IApiServiceError } from "store";

import {
  BenchmarkCalculatedField,
  BenchmarkDealWithResult,
  BenchmarkForecastGraph,
  BenchmarkForecastGraphName,
  BenchmarkLiquidityGraph,
  BenchmarkLiquidityGraphParams,
  BenchmarkQueries,
  DealGraph,
  GetReportsDataResponse,
  ReportFilters,
  ReportsQueries
} from "../../store/types";
import { useBenchmarkQueries } from "../query-context";

export const useGetBenchmarkFilters = (filters: ReportFilters): UseQueryResult<ReportFilters> => {
  const { getBenchmarkFilters } = useBenchmarkQueries();

  return useQuery<ReportFilters, IApiServiceError>(["benchmark-filters", filters], () => getBenchmarkFilters(filters));
};

export const useGetBenchmarkTable = (
  queries: Pick<
    ReportsQueries,
    "benchmarkTableColumns" | "filters" | "currency" | "exchangeMethod" | "groupBy" | "benchmarkIndexName"
  >
): UseQueryResult<GetReportsDataResponse> => {
  const { getBenchmarkTable } = useBenchmarkQueries();

  return useQuery<GetReportsDataResponse, IApiServiceError>(
    ["benchmark-table", queries],
    () => getBenchmarkTable(queries),
    {
      retry: (_: number, error: IApiServiceError) => {
        // Don't retry on validation errors (400 status) or client errors (4xx)
        if (error?.status || error?.response?.status) {
          return false;
        }
        return false;
      },
      keepPreviousData: true
    }
  );
};

// export const useGetBenchmarkTelescopeTable = (): UseQueryResult<TelescopeTable> => {
//   const { getBenchmarkTelescopeTable } = useBenchmarkQueries();

//   return useQuery(["benchmark-telescope-table"], getBenchmarkTelescopeTable);
// };

export const useGetBenchmarkGraph = (queries: BenchmarkQueries): UseQueryResult<DealGraph> => {
  const { getBenchmarkGraph } = useBenchmarkQueries();

  return useQuery<DealGraph, IApiServiceError>(["benchmark-graph", queries], () => getBenchmarkGraph(queries));
};

export const useGetBenchmarkForecastGraph = (
  queries: Pick<ReportsQueries, "filters" | "dateRange">,
  graphName: BenchmarkForecastGraphName
): UseQueryResult<BenchmarkForecastGraph> => {
  const { getBenchmarkForecastGraph } = useBenchmarkQueries();

  return useQuery<BenchmarkForecastGraph, IApiServiceError>(["benchmark-forecast", graphName, queries], () =>
    getBenchmarkForecastGraph(queries, graphName)
  );
};

export const useGetBenchmarkLiquidityGraph = (
  params: BenchmarkLiquidityGraphParams
): UseQueryResult<BenchmarkLiquidityGraph> => {
  const { getBenchmarkLiquidityGraph } = useBenchmarkQueries();

  return useQuery<BenchmarkLiquidityGraph, IApiServiceError>(["benchmark-liquidity-graph", params], () =>
    getBenchmarkLiquidityGraph(params)
  );
};

export const useGetBenchmarkTopPortfolios = (
  queries: Pick<ReportsQueries, "filters" | "dateRange">,
  field: BenchmarkCalculatedField
): UseQueryResult<BenchmarkDealWithResult[]> => {
  const { getBenchmarkTopPortfolios } = useBenchmarkQueries();

  return useQuery<BenchmarkDealWithResult[], IApiServiceError>(["benchmark-top-portfolios", queries, field], () =>
    getBenchmarkTopPortfolios(queries, field)
  );
};
